import React from "react";

import _ from "lodash";
import { DateTime } from "luxon";
import { connect } from "react-redux";
import { Alert, Button, Group, Menu } from "@mantine/core";
import { bindActionCreators } from "redux";
import TimesheetApi, { IPayEventResponse } from "../../data/api/timesheetApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { IPerson } from "../../types/persons/person";
import { ITimeSheetEntry } from "../../types/timesheet/Timesheet";
import { toDateTime } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";
import { SSTable } from "../common/TableData";

interface ITimeSheetAdminProps {
    persons: { [id: string]: IPerson }
    actions: {
    }
    // Direct props
    selectedAssociation: IAssociation | null;
}

const TimeSheetAdmin: React.FunctionComponent<ITimeSheetAdminProps> = (props) => {
    const { selectedAssociation } = props;

    const [unpaidEntries, setUnpaidEntries] = React.useState<ITimeSheetEntry[] | undefined>(undefined)
    const [persons, setPersons] = React.useState<{ [id: string]: IPerson }>({})
    const [events, setEvents] = React.useState<IPayEventResponse[]>([])

    const [selectedEntryIds, setSelectedEntryIds] = React.useState<Set<string>>(new Set())

    const [error, setError] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {

        if (!selectedAssociation) {
            setIsLoading(false)
            return
        }

        updateDataWithAssociationId(selectedAssociation._id)
    }, [selectedAssociation])

    function updateDataWithAssociationId(associationId: string) {
        setIsLoading(true)
        setError(null)
        TimesheetApi.getUnpaidTimesheetEntriesForAssociation(associationId)
            .then(resp => {
                setIsLoading(false)
                const personMap = _.keyBy(resp.persons || [], p => p._id)
                setUnpaidEntries(_.orderBy(
                    resp.data,
                    e => `${personMap[e.person].lastName}_${personMap[e.person].firstName}_${e.start}`
                ));
                setPersons({ ...personMap, ...props.persons })
            }).catch(er => {
                setIsLoading(false)
                setError(er.message)
            })

        TimesheetApi.getEventsByAssociationId(associationId)
            .then(resp => {
                setEvents(resp)
            }).catch(er => {
                setIsLoading(false)
                setError(er.message)

            })
    }



    if (!selectedAssociation) {
        return (
            <Alert color="blue">
                <h5>Select an Association</h5>
                <p>Please select an association from the dropdown above to manage timesheets.</p>
            </Alert>
        );
    }

    return <>
        <div className="p-4 mb-4 rounded shadow-sm border">

            {error && <Alert color="red">{error}</Alert>}
            {isLoading && <CenteredLoader />}

            <h3>Unpaid entries</h3>

            {unpaidEntries?.length === 0
                ? <p>No unpaid entries</p>
                : <div>
                    <Group>
                        <Button color="green" onClick={() => {
                            setIsLoading(true)
                            TimesheetApi.markAsPaid(
                                selectedAssociation._id!,
                                unpaidEntries!.map(e => e._id)
                            ).then(() => {
                                updateDataWithAssociationId(selectedAssociation._id!)
                            })
                        }}>Mark all as paid</Button>
                        <Menu>
                            <Menu.Target>
                                <Button color="gray" variant="outline" disabled={selectedEntryIds.size === 0}>
                                    Actions ⌄
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item onClick={() => {
                                    TimesheetApi.markAsPaid(
                                        selectedAssociation._id!,
                                        Array.from(selectedEntryIds)
                                    )
                                }}>Mark selected ({selectedEntryIds.size}) as paid</Menu.Item>
                                <Menu.Item onClick={() => {
                                    TimesheetApi.markAsPaid(
                                        selectedAssociation._id!,
                                        Array.from(selectedEntryIds)
                                    )
                                }}>Reject selected ({selectedEntryIds.size})</Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </Group>
                    <SSTable>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Person</th>
                                <th>Day</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {unpaidEntries?.map(e => {
                                const isChecked = selectedEntryIds.has(e._id);
                                return (<tr>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                                if (isChecked) {
                                                    setSelectedEntryIds(
                                                        new Set(Array.from(selectedEntryIds).filter(id => id !== e._id))
                                                    )
                                                } else {
                                                    setSelectedEntryIds(new Set(selectedEntryIds.add(e._id)))
                                                }
                                            }}
                                        />
                                    </td>
                                    <td>{persons[e.person]?.fullName}</td>
                                    <td>{toDateTime(e.start).toLocaleString(DateTime.DATE_MED)}</td>
                                    <td>{toDateTime(e.start).toLocaleString(DateTime.TIME_SIMPLE)}</td>
                                    <td>{toDateTime(e.end).toLocaleString(DateTime.TIME_SIMPLE)}</td>
                                    <td>${new Number(e.total).toFixed(2)}</td>
                                </tr>)
                            })}
                        </tbody>
                    </SSTable>
                </div>
            }


        </div>
        <br />

        <div className="p-4 rounded shadow-sm border">
            <h3>Pay events</h3>
            <SSTable>
                <thead>
                    <tr>
                        <th>Event</th>
                        <th>Amount</th>
                        <th>Paid on</th>
                        <th>Paid by</th>
                        <th>View</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map(e => <tr key={e._id}>
                        <td>${e.total.toFixed(2)}</td>
                        <td>{e.count} entries</td>
                        <td>{toDateTime(e.paidAt).toLocaleString(DateTime.DATE_MED)}</td>
                        <td>{persons[e.person]?.fullName}</td>
                        <td>
                            <a href={TimesheetApi.getPayEventPdfLink(e._id)} target="_blank">PDF</a>
                        </td>
                    </tr>)}
                </tbody>
            </SSTable>
        </div>
    </>
}

function mapStateToProps(state: IState, props: ITimeSheetAdminProps) {
    return {
        persons: state.persons.map,
    };
}

function mapDispatchToProps(dispatch) {

    return {
        actions: bindActionCreators({

        }, dispatch),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(TimeSheetAdmin);
